import React, { useState, useEffect } from 'react';
import { X, Save, Search, AlertCircle, CheckCircle, Users, Download, Loader2 } from 'lucide-react';
import assessmentService from '../../../services/assessmentService';
import { studentService } from '../../../services/studentService';
import { supabase } from '../../../lib/supabase';
import { getAssessmentNumber } from '../../../utils/assessmentNumbers';
import FormativeMarksheetModal from './FormativeMarksheetModal';
import { downloadFormativeMarksheetPdf } from '../utils/marksheetPdf';

const MarksEntryModal = ({
  isOpen,
  onClose,
  timeline,
  onSuccess,
  calendarPeriodName = '',
  calendarPeriod = null,
  calendarSemesters = []
}) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [rosterFilter, setRosterFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [periodInfo, setPeriodInfo] = useState({ academicYearName: '', semesterName: '' });
  const [schedules, setSchedules] = useState([]);
  const [isMarksheetOpen, setIsMarksheetOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [institutionSettings, setInstitutionSettings] = useState(null);
  const [unitHierarchy, setUnitHierarchy] = useState(null);

  useEffect(() => {
    if (isOpen && (timeline?.id || timeline?.unit?.id)) {
      loadStudentAssessments();
    }
  }, [isOpen, timeline]);

  useEffect(() => {
    const remediationOnly = Boolean(timeline?.reopened_for_remediation) && rosterFilter === 'failed';
    const periodStudents = remediationOnly
      ? students.filter(student => {
          const complete = schedules.length > 0 && schedules.every(schedule => {
            const value = student?.marks?.[schedule.id];
            return value !== '' && value !== null && value !== undefined && !isNaN(Number(value));
          });
          return complete && computeConvertedTotalMarks(student) < 30;
        })
      : students;

    if (!searchTerm) {
      setFilteredStudents(periodStudents);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = periodStudents?.filter(student =>
        student?.student?.full_name?.toLowerCase()?.includes(term) ||
        student?.student?.student_id?.toLowerCase()?.includes(term)
      );
      setFilteredStudents(filtered);
    }
  }, [students, searchTerm, rosterFilter, timeline?.reopened_for_remediation, schedules]);

  useEffect(() => {
    setRosterFilter(timeline?.reopened_for_remediation ? 'failed' : 'all');
  }, [timeline?.id, timeline?.reopened_for_remediation]);

  const loadStudentAssessments = async () => {
    setLoading(true);
    setError(null);

    try {
      // Clean up legacy non-isolated cache keys that lacked timeline_id scoping
      try {
        for (let k = localStorage.length - 1; k >= 0; k--) {
          const key = localStorage.key(k);
          if (key && key.startsWith('saved_mark_')) {
            const parts = key.split('_');
            if (parts.length < 4) {
              localStorage.removeItem(key);
            }
          }
        }
      } catch (e) {}

      const unitId = timeline?.unit?.id || timeline?.unit_id;
      const unitCode = timeline?.unit?.code;
      // A CAM can be linked to the current institution Calendar directly, or
      // to a legacy academic_semesters row. Resolve both links here because
      // registrations and older CAMs may still use the legacy id while the
      // current CAM windows use the Calendar id.
      const linkedCalendarPeriod = calendarPeriod
        || (timeline?.calendar_period?.id
          ? calendarSemesters.find(period => String(period.id) === String(timeline.calendar_period.id)) || timeline.calendar_period
          : null)
        || (() => {
          const legacy = timeline?.semester;
          if (!legacy || !calendarSemesters.length) return null;
          const exact = calendarSemesters.find(period =>
            String(period.start_date || '') === String(legacy.start_date || '') &&
            String(period.end_date || '') === String(legacy.end_date || '')
          );
          if (exact) return exact;
          const legacyStart = new Date(legacy.start_date);
          const legacyEnd = new Date(legacy.end_date);
          if ([legacyStart, legacyEnd].every(date => !Number.isNaN(date.getTime()))) {
            return calendarSemesters
              .filter(period => {
                const start = new Date(period.start_date);
                const end = new Date(period.end_date);
                return [start, end].every(date => !Number.isNaN(date.getTime()))
                  && start <= legacyEnd && end >= legacyStart;
              })
              .sort((left, right) => {
                const overlap = period => Math.max(0,
                  Math.min(new Date(period.end_date).getTime(), legacyEnd.getTime())
                  - Math.max(new Date(period.start_date).getTime(), legacyStart.getTime())
                );
                return overlap(right) - overlap(left);
              })[0] || null;
          }
          return null;
        })();
      const timelineCalendarSemesterId = timeline?.calendar_semester_id
        || timeline?.calendar_period?.id
        || linkedCalendarPeriod?.id
        || null;
      const timelineLegacySemesterId = timeline?.semester_id || timeline?.semester?.id || null;
      const timelineSemesterId = timelineCalendarSemesterId || timelineLegacySemesterId || null;
      let registrationSemesterIds = [...new Set([
        timelineLegacySemesterId,
        timelineCalendarSemesterId
      ].filter(Boolean).map(String))];
      let resolvedCalendarPeriod = linkedCalendarPeriod;

      // 1. Fetch active academic year and active semester
      let activeYearId = null;
      let activeSemesterId = null;
      try {
        const { data: yearRow } = await supabase
          .from('academic_years')
          .select('id, name, status')
          .ilike('status', 'active')
          .order('start_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (yearRow?.id) activeYearId = yearRow.id;

        let semRow = null;
        let academicSemesterError = null;
        if (timelineSemesterId) {
          const historicalSemester = await supabase
            .from('academic_semesters')
            .select('id, semester_number, semester_type, is_active, start_date, end_date')
            .eq('id', timelineLegacySemesterId || timelineSemesterId)
            .maybeSingle();
          semRow = historicalSemester.data;
          academicSemesterError = historicalSemester.error;
        } else {
          // My Units opens this modal with only a unit. Resolve the current
          // institution Calendar first; an old legacy row may still be marked
          // is_active even though its dates have ended.
          const currentCalendar = await supabase
            .from('semesters')
            .select('id, name, status, academic_year_id, start_date, end_date')
            .ilike('status', 'active')
            .gte('end_date', new Date().toISOString().slice(0, 10))
            .order('start_date', { ascending: false })
            .limit(1)
            .maybeSingle();
          semRow = currentCalendar.data;
          academicSemesterError = currentCalendar.error;
          if (academicSemesterError || !semRow) {
            const currentSemester = await supabase
              .from('academic_semesters')
              .select('id, semester_number, semester_type, is_active, start_date, end_date')
              .eq('is_active', true)
              .order('start_date', { ascending: false })
              .limit(1)
              .maybeSingle();
            semRow = currentSemester.data;
            academicSemesterError = currentSemester.error;
          }
        }
        if (academicSemesterError || !semRow) {
          let semQuery = supabase
            .from('semesters')
            .select('id, name, status, academic_year_id, start_date')
            .ilike('status', 'active');
          if (timelineCalendarSemesterId) semQuery = semQuery.eq('id', timelineCalendarSemesterId);
          else if (timelineLegacySemesterId) semQuery = semQuery.eq('id', timelineLegacySemesterId);
          else if (activeYearId) semQuery = semQuery.eq('academic_year_id', activeYearId);
          const fallbackSemester = await semQuery.order('start_date', { ascending: false }).limit(1).maybeSingle();
          semRow = fallbackSemester.data;
        }
        if (semRow?.id) activeSemesterId = semRow.id;
        if (!resolvedCalendarPeriod && semRow?.name && semRow?.start_date) {
          resolvedCalendarPeriod = semRow;
        }
        if (!registrationSemesterIds.length && activeSemesterId) {
          registrationSemesterIds = [String(activeSemesterId)];
        }

        setPeriodInfo({
          academicYearName: resolvedCalendarPeriod?.academic_year_name || yearRow?.name || '',
          semesterName: calendarPeriodName || semRow?.name || (semRow?.start_date ? `Academic period · ${semRow.start_date}` : 'Academic period')
        });
      } catch (ctxErr) {
        console.warn('Error fetching active year/semester:', ctxErr);
      }

      // Fetch institution settings & unit hierarchy (faculty, department, program)
      try {
        const { data: inst } = await supabase
          .from('institution_settings')
          .select('*')
          .limit(1)
          .maybeSingle();
        if (inst) setInstitutionSettings(inst);
      } catch (instErr) {}

      try {
        if (unitId) {
          const { data: uData } = await supabase
            .from('units')
            .select(`
              id, code, name,
              program:programs(
                id, name, code,
                department:departments(
                  id, name, code,
                  faculty:faculties(id, name, code)
                )
              )
            `)
            .eq('id', unitId)
            .maybeSingle();
          if (uData) setUnitHierarchy(uData);
        }
      } catch (uErr) {}

      // 2. Fetch Formative Assessment Schedules (Timelines) dynamically
      let schedList = [];
      try {
        // A combined-teaching group may share a classroom or trainer, but
        // marks still belong to the selected unit. Keep the assessment
        // windows unit-scoped so another unit's CAM timeline cannot be used
        // as this unit's CAM1/CAM2/CAM3 column.
        const relatedUnitIds = new Set([unitId].filter(Boolean).map(String));
        let scoped = await supabase.rpc('get_unit_formative_assessment_windows_v2', {
          target_unit: String(unitId || unitCode)
        });
        if (scoped.error) {
          // Keep compatibility with deployments that have the original
          // authorised roster RPC but not its v2 replacement yet.
          scoped = await supabase.rpc('get_unit_formative_assessment_windows', {
            target_unit: String(unitId || unitCode)
          });
        }
        let allTimelines = !scoped.error && Array.isArray(scoped.data) ? scoped.data : null;
        const serviceResult = await assessmentService.timeline.getTimelines({ assessment_type: 'formative' });
        if (!serviceResult.error && Array.isArray(serviceResult.data)) {
          const merged = [...(allTimelines || []), ...serviceResult.data];
          allTimelines = [...new Map(merged.filter(item => item?.id).map(item => [String(item.id), item])).values()];
        } else if (serviceResult.error) {
          // The nested legacy semester relation can be unavailable on a
          // partially migrated deployment. Still load the direct rows so a
          // valid active CAM is not hidden from mark entry.
          const direct = await supabase
            .from('assessment_timelines')
            .select('*')
            .eq('assessment_type', 'formative');
          if (!direct.error && Array.isArray(direct.data)) {
            const merged = [...(allTimelines || []), ...direct.data];
            allTimelines = [...new Map(merged.filter(item => item?.id).map(item => [String(item.id), item])).values()];
          }
        }
        if (unitId && (!allTimelines || !allTimelines.some(item => String(item?.unit_id || '') === String(unitId)))) {
          // Last-resort direct lookup for the My Units entry point. It avoids
          // losing valid CAMs when an RPC returns an institution-wide payload
          // without the unit assignment fields.
          const directUnit = await supabase
            .from('assessment_timelines')
            .select('*')
            .eq('assessment_type', 'formative')
            .eq('unit_id', unitId);
          if (!directUnit.error && Array.isArray(directUnit.data)) {
            const merged = [...(allTimelines || []), ...directUnit.data];
            allTimelines = [...new Map(merged.filter(item => item?.id).map(item => [String(item.id), item])).values()];
          }
        }
        if (allTimelines && allTimelines.length > 0) {
          const targetPeriodIds = new Set([
            ...registrationSemesterIds,
            timeline?.semester_id,
            timeline?.semester?.id,
            timeline?.calendar_semester_id,
            timeline?.calendar_period?.id,
            linkedCalendarPeriod?.id,
            activeSemesterId
          ].filter(Boolean).map(String));
          const calendarStart = resolvedCalendarPeriod?.start_date ? new Date(resolvedCalendarPeriod.start_date) : null;
          const calendarEnd = resolvedCalendarPeriod?.end_date ? new Date(resolvedCalendarPeriod.end_date) : null;
          const periodTimelines = allTimelines.filter(t => {
            const periodIds = [t.calendar_semester_id, t.semester_id].filter(Boolean).map(String);
            const idMatches = !targetPeriodIds.size || !periodIds.length || periodIds.some(id => targetPeriodIds.has(id));
            if (idMatches) return true;
            // Older CAMs may still carry a legacy semester id even though
            // their assessment window dates were created for the current
            // Calendar period. Keep those only when the CAM dates overlap
            // the selected Calendar period.
            if (!calendarStart || !calendarEnd) return false;
            const camStart = new Date(t.start_date);
            const camEnd = new Date(t.end_date || t.submission_deadline);
            return [camStart, camEnd].every(date => !Number.isNaN(date.getTime()))
              && camStart <= calendarEnd && camEnd >= calendarStart;
          });
          const matching = periodTimelines.filter(t => {
            const timelineUnitId = t.unit_id == null ? '' : String(t.unit_id);
            return relatedUnitIds.has(timelineUnitId) ||
              (unitCode && (timelineUnitId === String(unitCode) || String(t.unit?.code || '').toLowerCase() === String(unitCode).toLowerCase())) ||
              (!timelineUnitId && String(t.id) === String(timeline?.id));
          });
          // Always retain the CAM that opened this modal. This is important
          // for a newly Calendar-linked CAM while the RPC/service cache is
          // still returning the older legacy semester shape.
          if (timeline?.id && !matching.some(item => String(item.id) === String(timeline.id))) {
            matching.push({ ...timeline, unit_id: timeline.unit_id || unitId });
          }
          const sequence = new Map();
          matching.forEach(item => {
            const number = getAssessmentNumber(item, 0);
            if (number) sequence.set(number, item);
          });
          schedList = [...sequence.values()];
        }
      } catch (tErr) {
        console.warn('Error fetching assessment schedules:', tErr);
      }

      if (schedList.length === 0) {
        schedList = timeline?.id ? [timeline] : [];
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const processedSchedules = schedList.map(s => {
        const closingDateStr = s.end_date || s.submission_deadline || s.closing_date;
        const scheduleStatus = String(s.assessment_status || s.status || s.window_status || '').toLowerCase();
        let isClosed = ['closed', 'completed', 'locked', 'archived'].includes(scheduleStatus);
        if (s.reopened_for_remediation && ['active', 'scheduled'].includes(scheduleStatus)) {
          isClosed = false;
        }
        if (closingDateStr) {
          const d = new Date(closingDateStr);
          d.setHours(23, 59, 59, 999);
          isClosed = isClosed || (today > d && !s.reopened_for_remediation);
        }
        return {
          id: s.id,
          title: s.title || `CAM${s.assessment_number || 1}`,
          assessment_number: getAssessmentNumber(s, 1),
          total_marks: s.total_marks || 100,
          endDate: closingDateStr,
          reopened_for_remediation: Boolean(s.reopened_for_remediation),
          isClosed
        };
      }).sort((a, b) => a.assessment_number - b.assessment_number || String(a.title).localeCompare(String(b.title)));

      setSchedules(processedSchedules);

      // 3. Find registered students in student_unit_registrations for this unit
      let regRows = [];
      let targetUnitIds = [String(unitId)];
      if (unitCode) {
        try {
          const { data: codeUnits } = await supabase
            .from('units')
            .select('id')
            .eq('code', unitCode);
          if (codeUnits) {
            codeUnits.forEach(u => targetUnitIds.push(String(u.id)));
          }
        } catch (e) {}
      }
      targetUnitIds = [...new Set(targetUnitIds.filter(Boolean))];

      try {
        let regQuery = supabase
          .from('student_unit_registrations')
          .select('id, student_id, unit_id, academic_year_id, semester_id, enrollment_status, grade, grade_letter');

        if (targetUnitIds.length === 1) {
          regQuery = regQuery.eq('unit_id', targetUnitIds[0]);
        } else {
          regQuery = regQuery.in('unit_id', targetUnitIds);
        }
        if (registrationSemesterIds.length === 1) regQuery = regQuery.eq('semester_id', registrationSemesterIds[0]);
        else if (registrationSemesterIds.length > 1) regQuery = regQuery.in('semester_id', registrationSemesterIds);

        const { data: directRegs } = await regQuery;
        if (directRegs && directRegs.length > 0) {
          regRows = directRegs;
        } else {
          let fallbackRegsQuery = supabase
            .from('student_unit_registrations')
            .select('id, student_id, unit_id, academic_year_id, semester_id, enrollment_status, grade, grade_letter');
          if (registrationSemesterIds.length === 1) fallbackRegsQuery = fallbackRegsQuery.eq('semester_id', registrationSemesterIds[0]);
          else if (registrationSemesterIds.length > 1) fallbackRegsQuery = fallbackRegsQuery.in('semester_id', registrationSemesterIds);
          const { data: allRegs } = await fallbackRegsQuery;

          if (allRegs && allRegs.length > 0) {
            const matched = allRegs.filter(r => 
              targetUnitIds.includes(String(r.unit_id)) ||
              String(r.unit_id).toLowerCase() === String(unitCode).toLowerCase()
            );
            if (matched.length > 0) regRows = matched;
          }
        }
      } catch (e) {
        console.warn('Error querying registrations:', e);
      }

      // Cross-department trainers may be blocked by student/profile RLS even
      // though they are actively assigned to teach the unit. Use the scoped
      // roster RPC to resolve only students registered for this unit.
      let rosterByStudent = {};
      try {
        const { data: rosterRows, error: rosterError } = await supabase.rpc('get_unit_student_roster', {
          target_unit: String(unitId || unitCode)
        });
        if (!rosterError && Array.isArray(rosterRows) && rosterRows.length) {
          const scopedRosterRows = registrationSemesterIds.length
            ? rosterRows.filter(row => !row.semester_id || registrationSemesterIds.includes(String(row.semester_id)))
            : rosterRows;
          regRows = scopedRosterRows.map(row => ({
            id: row.registration_id,
            student_id: row.student_id,
            unit_id: row.unit_id,
            academic_year_id: row.academic_year_id,
            semester_id: row.semester_id,
            enrollment_status: row.enrollment_status,
            grade: row.grade,
            grade_letter: row.grade_letter
          }));
          rosterByStudent = Object.fromEntries(scopedRosterRows.map(row => [String(row.student_id), row]));
        }
      } catch (e) {
        console.warn('Scoped unit roster unavailable:', e);
      }

      let filteredRegs = (regRows || []).filter(r => {
        const st = String(r.enrollment_status || '').toLowerCase();
        return st === 'enrolled' || st === 'completed' || st === 'active';
      });

      let sids = [...new Set(filteredRegs.map(r => r.student_id).filter(Boolean))];

      if (sids.length === 0) {
        setStudents([]);
        setFilteredStudents([]);
        setLoading(false);
        return;
      }

      // 4. Fetch details for each student: real registration number and real name
      const studentDetails = await Promise.all(sids.map(async (sid) => {
        const rosterStudent = rosterByStudent[String(sid)];
        let admNo = rosterStudent?.admission_number || null;
        let fullName = rosterStudent?.full_name || null;

        // A. user_profiles by id
        try {
          const { data: prof } = await supabase
            .from('user_profiles')
            .select('id, full_name, student_id, email')
            .eq('id', sid)
            .maybeSingle();

          if (prof) {
            if (prof.full_name) fullName = prof.full_name;
            if (prof.student_id) admNo = prof.student_id;
          }
        } catch (e) {}

        // B. students table by profile_id OR id
        try {
          const { data: st } = await supabase
            .from('students')
            .select('*')
            .or(`profile_id.eq.${sid},id.eq.${sid}`)
            .maybeSingle();

          if (st) {
            if (!admNo && st.admission_number) admNo = st.admission_number;
            if (!admNo && st.student_id) admNo = st.student_id;
            if (!fullName) {
              const n = [st.first_name, st.middle_name, st.last_name].filter(Boolean).join(' ') || st.full_name;
              if (n) fullName = n;
            }
          }
        } catch (e) {}

        // C. admission_applications
        try {
          const { data: app } = await supabase
            .from('admission_applications')
            .select('*')
            .or(`id.eq.${sid}${admNo ? `,registration_number.eq.${admNo},application_number.eq.${admNo}` : ''}`)
            .maybeSingle();

          if (app) {
            if (!admNo && (app.registration_number || app.application_number)) {
              admNo = app.registration_number || app.application_number;
            }
            if (!fullName) {
              const an = [app.first_name, app.middle_name, app.last_name].filter(Boolean).join(' ');
              if (an) fullName = an;
            }
          }
        } catch (e) {}

        // D. studentService.getStudentById
        if (!fullName || !admNo) {
          try {
            const fullSt = await studentService.getStudentById(sid);
            if (fullSt) {
              if (!admNo && (fullSt.admission_number || fullSt.student_id)) {
                admNo = fullSt.admission_number || fullSt.student_id;
              }
              if (!fullName && fullSt.full_name) {
                fullName = fullSt.full_name;
              }
            }
          } catch (e) {}
        }

        return {
          sid,
          // Never manufacture a registration number or student name. Missing
          // identity data should remain visible as missing and be corrected in
          // the student record rather than becoming a fake learner.
          regNumber: admNo || null,
          fullName: fullName || null
        };
      }));

      // 5. Fetch existing marks from student_assessments and registrations
      let assessRows = [];
      try {
        const schedIds = processedSchedules.map(s => s.id).filter(id => !String(id).startsWith('default-'));
        const regIds = regRows.map(r => r.id).filter(Boolean);

        const [scopedMarks, byStudent, byTimeline, byReg, byRecent] = await Promise.all([
          supabase.rpc('get_unit_formative_marks', { target_unit: String(unitId || unitCode) }),
          sids.length ? supabase.from('student_assessments').select('*').in('student_id', sids) : { data: [] },
          schedIds.length ? supabase.from('student_assessments').select('*').in('timeline_id', schedIds) : { data: [] },
          regIds.length ? supabase.from('student_assessments').select('*').in('unit_registration_id', regIds) : { data: [] },
          supabase.from('student_assessments').select('*').order('created_at', { ascending: false }).limit(200)
        ]);

        const allFetched = [
          ...(!scopedMarks.error && Array.isArray(scopedMarks.data) ? scopedMarks.data : []),
          ...(byStudent.data || []),
          ...(byTimeline.data || []),
          ...(byReg.data || []),
          ...(byRecent.data || [])
        ];

        const dedup = new Map();
        allFetched.forEach(item => {
          if (item && item.id) dedup.set(String(item.id), item);
        });
        assessRows = Array.from(dedup.values());
        
        // --- Fetch POE Evidence for these assessments ---
        if (assessRows.length > 0) {
          try {
            const assessmentIds = assessRows.map(a => a.id).filter(Boolean);
            // Chunking in case of large lists
            const chunkSize = 200;
            for (let i = 0; i < assessmentIds.length; i += chunkSize) {
              const chunk = assessmentIds.slice(i, i + chunkSize);
              const { data: evs, error } = await supabase
                .rpc('get_assessments_evidence_stages', { p_assessment_ids: chunk });
                
              if (error) {
                console.warn('Error fetching evidence RPC:', error);
                continue;
              }
                
              if (evs && evs.length > 0) {
                // Map evidence back to assessment
                evs.forEach(ev => {
                  const target = assessRows.find(a => String(a.id) === String(ev.student_assessment_id));
                  if (target) {
                    if (!target.evidence) target.evidence = [];
                    target.evidence.push(ev);
                  }
                });
              }
            }
          } catch (e) {
            console.warn('Error fetching evidence:', e);
          }
        }
      } catch (e) {
        console.warn('Error querying student_assessments:', e);
      }

      const regMap = Object.fromEntries(regRows.map(r => [r.student_id, r.id]));
      const currentTimelineIds = new Set(
        processedSchedules
          .map(schedule => schedule.id)
          .filter(id => id && !String(id).startsWith('default-'))
          .map(String)
      );

      // 6. Build mapped students with accurately restored marks
      const mapped = studentDetails.map(({ sid, regNumber, fullName }) => {
        const candidateIds = [sid, regNumber].filter(Boolean).map(String);

        const studentAssessments = assessRows.filter(a => {
          const matchSid = a.student_id && candidateIds.some(c => String(c).toLowerCase() === String(a.student_id).toLowerCase());
          const matchReg = a.unit_registration_id && regMap[sid] && String(a.unit_registration_id) === String(regMap[sid]);
          const matchTimeline = a.timeline_id && currentTimelineIds.has(String(a.timeline_id));
          // A student's ID alone is not enough: the same student can have
          // marks for many units. Require this unit's registration or one of
          // this unit's assessment timelines.
          return matchReg || (matchSid && matchTimeline);
        });
        
        // Map marks strictly per schedule ID
        const marksBySchedule = {};
        const poeBySchedule = {};
        const assignedAssessmentIds = new Set();

        processedSchedules.forEach((sched, sIdx) => {
          // Match a persisted timeline first. Only orphaned legacy rows may
          // fall back to assessment_number, and each row may be used once.
          const existing = studentAssessments.find(a =>
            !assignedAssessmentIds.has(String(a.id)) &&
            a.timeline_id && String(a.timeline_id) === String(sched.id)
          ) || studentAssessments.find(a =>
            !assignedAssessmentIds.has(String(a.id)) &&
            !a.timeline_id &&
            Number(a.assessment_number) === Number(sched.assessment_number || (sIdx + 1))
          );

          if (existing?.id) assignedAssessmentIds.add(String(existing.id));

          let markVal = null;
          let poeStatus = 'not_uploaded';

          if (existing) {
            if (existing.raw_marks !== null && existing.raw_marks !== undefined) {
              markVal = Number(existing.raw_marks);
            } else if (existing.converted_marks !== null && existing.converted_marks !== undefined) {
              markVal = Number(existing.converted_marks);
            }
            
            // Check POE status
            if (existing.evidence && existing.evidence.length > 0) {
              // Sort by uploaded_at descending to get latest
              const sortedEv = [...existing.evidence].sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
              poeStatus = sortedEv[0].workflow_stage;
            }
          }

          marksBySchedule[sched.id] = (markVal !== null && markVal !== undefined && !isNaN(markVal))
            ? Math.min(30, Math.max(0, markVal))
            : '';
            
          poeBySchedule[sched.id] = poeStatus;
        });

        const primaryExisting = studentAssessments[0];

        return {
          id: primaryExisting ? primaryExisting.id : `new-${sid}`,
          unit_registration_id: regMap[sid] || null,
          student: {
            id: sid,
            profile_id: sid,
            student_id: regNumber,
            full_name: fullName
          },
          marks: marksBySchedule,
          poe: poeBySchedule,
          hasChanges: false
        };
      });

      setStudents(mapped);
      setFilteredStudents(mapped);

    } catch (err) {
      console.error('Error loading assessments:', err);
      setError(err?.message || 'Failed to load enrolled students');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleMarksChange = (studentId, scheduleId, value) => {
    let numericVal = '';
    if (value !== '') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        numericVal = Math.min(30, Math.max(0, parsed));
      }
    }

    const updateList = prev => prev.map(item => {
      const matchId = String(item.student.id) === String(studentId) ||
                      String(item.student.profile_id) === String(studentId) ||
                      String(item.student.student_id) === String(studentId);
      if (matchId) {
        const updatedMarks = {
          ...item.marks,
          [scheduleId]: numericVal
        };
        return {
          ...item,
          marks: updatedMarks,
          hasChanges: true
        };
      }
      return item;
    });

    setStudents(updateList);
    setFilteredStudents(updateList);
  };

  const computeRawTotalMarks = (item) => {
    if (!item?.marks) return 0;
    const sum = Object.values(item.marks).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
    return Math.round(sum * 10) / 10;
  };

  const computeConvertedTotalMarks = (item) => {
    if (!item?.marks || schedules.length === 0) return 0;
    const rawSum = computeRawTotalMarks(item);
    const maxRaw = schedules.length * 30;
    if (maxRaw <= 0) return 0;
    const converted = (rawSum / maxRaw) * 60;
    return Math.round(converted);
  };

  const handleSaveAllMarks = async () => {
    const unitId = timeline?.unit?.id || timeline?.unit_id;
    const studentsToSave = students?.filter(s => s?.hasChanges || (s?.marks && Object.values(s.marks).some(v => v !== '' && v !== null))) || [];
    if (studentsToSave.length === 0) {
      setError('No marks available to save');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const numSchedules = schedules.length || 1;
      let anyError = null;

      for (const student of studentsToSave) {
        const studentProfileId = student.student.profile_id || student.student.id;
        const overallConverted = computeConvertedTotalMarks(student);
        const percentage = (overallConverted / 60) * 100;

        let rating = '1';
        let grade_letter = 'F';
        if (percentage >= 80) { rating = '5'; grade_letter = 'A'; }
        else if (percentage >= 65) { rating = '4'; grade_letter = 'B'; }
        else if (percentage >= 50) { rating = '3'; grade_letter = 'C'; }
        else if (percentage >= 30) { rating = '2'; grade_letter = 'D'; }

        for (let sIdx = 0; sIdx < schedules.length; sIdx++) {
          const sched = schedules[sIdx];
          const rawMark = Math.min(30, Math.max(0, parseFloat(student.marks?.[sched.id]) || 0));
          const weightPerSched = 60 / numSchedules;
          const converted = Math.round((rawMark / 30) * weightPerSched * 100) / 100;
          
          const assessmentNum = getAssessmentNumber(sched, sIdx + 1);

          console.log('[SaveMarks] Processing:', {
            studentProfileId,
            schedId: sched.id,
            assessmentNum,
            rawMark,
            converted,
            unit_registration_id: student.unit_registration_id
          });

          const scopedPayload = {
            unit_registration_id: student.unit_registration_id || null,
            student_id: studentProfileId,
            timeline_id: String(sched.id).startsWith('default-') ? null : sched.id,
            assessment_number: assessmentNum,
            raw_marks: rawMark,
            converted_marks: converted,
            rating,
            grade_letter
          };
          const scopedSave = await supabase.rpc('save_unit_formative_mark', { payload: scopedPayload });
          if (!scopedSave.error) continue;
          if (scopedSave.error.code !== 'PGRST202' && !/could not find.*function/i.test(scopedSave.error.message || '')) {
            anyError = scopedSave.error;
            continue;
          }

          // Check if assessment record already exists
          let existing = null;
          if (!String(sched.id).startsWith('default-')) {
            const { data: byTimeline } = await supabase
              .from('student_assessments')
              .select('id')
              .eq('student_id', studentProfileId)
              .eq('timeline_id', sched.id)
              .maybeSingle();
            if (byTimeline) existing = byTimeline;
          }

          if (!existing && student.unit_registration_id) {
            const { data: byReg } = await supabase
              .from('student_assessments')
              .select('id')
              .eq('unit_registration_id', student.unit_registration_id)
              .eq('assessment_number', assessmentNum)
              .maybeSingle();
            if (byReg) existing = byReg;
          }

          if (existing?.id) {
            const { error: updateError } = await supabase
              .from('student_assessments')
              .update({
                raw_marks: rawMark,
                converted_marks: converted,
                rating,
                grade_letter,
                submission_status: 'submitted',
                assessment_number: assessmentNum,
                updated_at: new Date().toISOString()
              })
              .eq('id', existing.id);

            if (updateError) {
              console.error('[SaveMarks] UPDATE error:', updateError.message, updateError.code);
              anyError = updateError;
            } else {
              console.log('[SaveMarks] UPDATE success for student', studentProfileId);
            }
          } else {
            const insertPayload = {
              unit_registration_id: student.unit_registration_id || null,
              student_id: studentProfileId,
              timeline_id: String(sched.id).startsWith('default-') ? null : sched.id,
              assessment_number: assessmentNum,
              raw_marks: rawMark,
              converted_marks: converted,
              rating,
              grade_letter,
              submission_status: 'submitted',
              submitted_at: new Date().toISOString()
            };
            console.log('[SaveMarks] INSERT payload:', insertPayload);

            const { data: insertedData, error: insertError } = await supabase
              .from('student_assessments')
              .insert(insertPayload)
              .select();

            if (insertError) {
              console.error('[SaveMarks] INSERT error:', insertError.message, insertError.code);
              anyError = insertError;
            } else {
              console.log('[SaveMarks] INSERT success:', insertedData);
            }
          }
        }
      }

      if (anyError) {
        setError(`Save partially failed: ${anyError.message} (${anyError.code}). Check browser console for details.`);
      } else {
        setSuccess('All marks saved successfully.');
        setTimeout(() => setSuccess(null), 3000);
      }

      const resetStudents = prev => prev.map(s => ({ ...s, hasChanges: false }));
      setStudents(resetStudents);
      setFilteredStudents(resetStudents);
      onSuccess?.();
    } catch (err) {
      console.error('Error saving all marks:', err);
      setError(err?.message || 'Failed to save marks');
    } finally {
      setSaving(false);
    }
  };




  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-[#f08c1a] text-white p-3 flex justify-between items-center">
          <h2 className="text-lg font-medium">Select Details</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Info Section */}
        <div className="p-4 border-b border-gray-200">
          <p className="text-gray-800 text-lg">
            Enter Marks for Unit: <span className="font-semibold text-[#1f874c]">{timeline?.unit?.code} - {timeline?.unit?.name}</span>
            {periodInfo.semesterName ? ` | Semester: ${periodInfo.semesterName}` : ''}
            {periodInfo.academicYearName ? ` (${periodInfo.academicYearName})` : ''}
          </p>
          <div className="mt-2 text-[#d35400] text-sm font-medium flex flex-wrap items-center gap-4">
            <p className="uppercase font-semibold">Continuous Assessment: Capped at Max 30 per CAM/CAT</p>
            <span className="bg-orange-100 text-orange-900 px-2.5 py-0.5 rounded text-xs font-semibold">
              Converted Total Formative Marks: Max 60 ({schedules.length} CAM{schedules.length === 1 ? '' : 's'} Done)
            </span>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">×</button>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-green-700 text-sm">{success}</span>
          </div>
        )}

        {/* Controls */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
          <div className="flex flex-wrap items-center gap-2">
            {timeline?.reopened_for_remediation && (
              <select
                value={rosterFilter}
                onChange={e => setRosterFilter(e.target.value)}
                className="rounded border border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-900 focus:outline-none focus:ring-1 focus:ring-violet-500"
                aria-label="Filter remediation roster"
              >
                <option value="failed">Failed formative learners</option>
                <option value="all">All registered learners</option>
              </select>
            )}
            <button className="bg-[#1f874c] text-white px-4 py-2 rounded shadow-sm text-sm flex items-center hover:bg-green-700">
               Upload In Excel
            </button>
            <button 
              type="button"
              disabled={isDownloading}
              onClick={async () => {
                setIsDownloading(true);
                try {
                  await downloadFormativeMarksheetPdf({
                    institution: institutionSettings,
                    facultyName: unitHierarchy?.program?.department?.faculty?.name || '',
                    departmentName: unitHierarchy?.program?.department?.name || '',
                    programName: unitHierarchy?.program?.name || timeline?.unit?.program?.name || '',
                    unitName: timeline?.unit?.name || unitHierarchy?.name || '',
                    unitCode: timeline?.unit?.code || unitHierarchy?.code || '',
                    semesterName: periodInfo.semesterName,
                    academicYear: periodInfo.academicYearName,
                    schedules,
                    students: filteredStudents
                  });
                } catch (err) {
                  console.error('PDF error:', err);
                  alert('Failed to generate PDF. Please try again.');
                } finally {
                  setIsDownloading(false);
                }
              }}
              className="bg-[#337ab7] hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait text-white px-4 py-2 rounded shadow-sm text-sm flex items-center gap-1.5 transition-colors"
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isDownloading ? 'Generating PDF...' : 'Download Marksheet'}
            </button>
            <button
              onClick={handleSaveAllMarks}
              disabled={saving || !students?.some(s => s?.hasChanges)}
              className="bg-blue-600 text-white px-4 py-2 rounded shadow-sm text-sm flex items-center hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Marks
            </button>
          </div>
          <div className="flex border border-gray-300 rounded overflow-hidden">
            <input
              type="text"
              placeholder="Enter No/Name to Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="px-3 py-1.5 text-sm w-64 focus:outline-none"
            />
            <div className="bg-[#337ab7] text-white px-3 py-1.5 flex items-center justify-center">
              <Search className="h-4 w-4" />
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredStudents?.length > 0 ? (
            <div className="overflow-x-auto p-4">
              <table className="w-full border-collapse">
                <thead className="bg-[#1f874c] text-white">
                  <tr>
                    <th className="text-left py-2.5 px-4 text-sm font-semibold border border-[#1f874c]">#</th>
                    <th className="text-left py-2.5 px-4 text-sm font-semibold border border-[#1f874c]">Student No.</th>
                    <th className="text-left py-2.5 px-4 text-sm font-semibold border border-[#1f874c]">Full Names</th>
                    {schedules.map(sched => (
                      <th key={sched.id} className="text-center py-2.5 px-4 text-sm font-semibold uppercase border border-[#1f874c]">
                        <div className="flex items-center justify-center gap-1.5">
                          <span>{sched.title} (/30)</span>
                          {sched.isClosed && (
                            <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded font-normal uppercase tracking-wider">
                              Closed
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="text-center py-2.5 px-4 text-sm font-semibold border border-[#1f874c]">Total Formative (/60)</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredStudents.map((studentAssessment, index) => (
                    <tr 
                      key={studentAssessment.id} 
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2.5 px-4 text-sm text-gray-700">{index + 1}</td>
                      <td className="py-2.5 px-4 text-sm font-medium text-gray-900">
                        {studentAssessment.student.student_id}
                      </td>
                      <td className="py-2.5 px-4 text-sm text-gray-800">
                        {studentAssessment.student.full_name}
                      </td>
                      {schedules.map(sched => {
                        const marksVal = studentAssessment.marks?.[sched.id];
                        return (
                          <td key={sched.id} className="py-2.5 px-4 text-center">
                            <input
                              type="number"
                              value={marksVal !== undefined && marksVal !== null ? marksVal : ''}
                              onChange={(e) => handleScheduleMarksChange(studentAssessment.student.id, sched.id, e.target.value)}
                              min="0"
                              max="30"
                              step="0.5"
                              disabled={sched.isClosed}
                              placeholder="0"
                              title={sched.isClosed ? `Submission closed on ${new Date(sched.endDate).toLocaleDateString()}` : 'Cap: Max 30'}
                              className={`w-16 text-center border-2 rounded px-1 py-1 text-sm font-bold focus:outline-none transition-colors ${
                                sched.isClosed
                                  ? 'bg-gray-100 text-gray-900 border-gray-300 cursor-not-allowed'
                                  : 'border-blue-600 text-blue-800 focus:ring-1 focus:ring-blue-600 bg-white'
                              }`}
                            />
                          </td>
                        );
                      })}
                      <td className="py-2.5 px-4 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <span className="text-sm font-bold text-[#1f874c]">
                            {computeConvertedTotalMarks(studentAssessment)} / 60
                          </span>
                          <span className="text-[11px] text-gray-500 font-medium">
                            Raw: {computeRawTotalMarks(studentAssessment)} / {schedules.length * 30}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {timeline?.reopened_for_remediation && rosterFilter === 'failed' ? 'No failed formative learners found' : 'No registered students found'}
              </h3>
              <p className="text-gray-600">
                {timeline?.reopened_for_remediation && rosterFilter === 'failed'
                  ? `No learners with a complete formative mark below 30/60 were found for ${periodInfo.semesterName || 'this semester'}.`
                  : `No students have registered for this unit in ${periodInfo.semesterName || 'this semester'} ${periodInfo.academicYearName ? `(${periodInfo.academicYearName})` : ''}.`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Official Printable Formative Marksheet */}
      {isMarksheetOpen && (
        <FormativeMarksheetModal
          isOpen={isMarksheetOpen}
          onClose={() => setIsMarksheetOpen(false)}
          institution={institutionSettings}
          facultyName={unitHierarchy?.program?.department?.faculty?.name || 'Faculty of Science and Technology'}
          departmentName={unitHierarchy?.program?.department?.name || 'Department of Information Communication Technology'}
          programName={unitHierarchy?.program?.name || timeline?.unit?.program?.name || 'Diploma in Information Communication Technology'}
          unitName={timeline?.unit?.name || unitHierarchy?.name || ''}
          unitCode={timeline?.unit?.code || unitHierarchy?.code || ''}
          semesterName={periodInfo.semesterName}
          academicYear={periodInfo.academicYearName}
          schedules={schedules}
          students={filteredStudents}
        />
      )}
    </div>
  );
};

export default MarksEntryModal;
